import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchStore, fetchProducts } from "@/lib/api";
import ProductCard from "@/components/shared/ProductCard";
import {
    MapPin,
    Star,
    Package,
    Search,
    Heart,
    Truck,
    CreditCard,
    Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { motion } from "framer-motion";

export default function StorePage() {
    const { storeSlug } = useParams();
    const [store, setStore] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterCategory, setFilterCategory] = useState("all");
    const [sort, setSort] = useState("recent");
    const [q, setQ] = useState("");

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchStore(storeSlug), fetchProducts({ store_slug: storeSlug })])
            .then(([s, p]) => {
                setStore(s);
                setProducts(p);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [storeSlug]);

    // Update document.title and meta description so Google (which executes JS)
    // and the browser tab show the right info per store.
    useEffect(() => {
        if (!store) return;
        const baseTitle = "Tiendify";
        document.title = `${store.name} — ${baseTitle}`;
        const desc = store.shortDescription || store.description || "";
        let m = document.querySelector('meta[name="description"]');
        if (!m) {
            m = document.createElement("meta");
            m.setAttribute("name", "description");
            document.head.appendChild(m);
        }
        m.setAttribute("content", desc.slice(0, 160));
        return () => {
            document.title = `${baseTitle} — Crea tu tienda en línea, sin renta mensual`;
        };
    }, [store]);

    const filtered = useMemo(() => {
        let list = [...products];
        if (q) {
            const qq = q.toLowerCase();
            list = list.filter(
                (p) =>
                    p.name.toLowerCase().includes(qq) ||
                    p.description.toLowerCase().includes(qq)
            );
        }
        if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
        else if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
        else if (sort === "popular")
            list.sort((a, b) => b.salesCount - a.salesCount);
        else
            list.sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
        return list;
    }, [products, q, sort]);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center text-zinc-500 text-sm">
                Cargando tienda…
            </div>
        );
    }

    if (!store) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
                <p className="text-lg font-semibold">Tienda no encontrada</p>
                <Link
                    to="/"
                    className="mt-4 inline-block text-sm text-indigo-600 hover:underline"
                >
                    Volver al inicio
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="store-page">
            {/* Banner */}
            <div className="relative">
                <div className="aspect-[16/5] sm:aspect-[16/4] w-full bg-zinc-100 overflow-hidden">
                    <img
                        src={store.banner}
                        alt={`${store.name} banner`}
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent" />
            </div>

            {/* Header */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="-mt-16 sm:-mt-20 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8 shadow-sm"
                    >
                        <div className="flex flex-col sm:flex-row gap-5 sm:items-end justify-between">
                            <div className="flex gap-4 sm:gap-5 items-end">
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border-4 border-white shadow-md overflow-hidden bg-zinc-100 shrink-0">
                                    <img
                                        src={store.logo}
                                        alt={store.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
                                        {store.name}
                                    </h1>
                                    <p className="text-sm text-zinc-600 mt-1 max-w-xl">
                                        {store.shortDescription}
                                    </p>
                                    <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-zinc-500">
                                        <span className="flex items-center gap-1">
                                            <MapPin className="w-3.5 h-3.5" />
                                            {store.location?.city},{" "}
                                            {store.location?.state}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                                            <span className="font-semibold text-zinc-700">
                                                {store.rating?.toFixed(1)}
                                            </span>{" "}
                                            ({store.reviewsCount})
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Package className="w-3.5 h-3.5" />
                                            {store.productsCount} productos
                                        </span>
                                        <span>· {store.salesCount} ventas</span>
                                    </div>
                                </div>
                            </div>
                            <Button
                                data-testid="store-follow-button"
                                variant="outline"
                                className="border-zinc-200"
                            >
                                <Heart className="w-4 h-4 mr-1.5" />
                                Seguir tienda
                            </Button>
                        </div>

                        <p className="mt-6 text-sm text-zinc-600 leading-relaxed max-w-3xl">
                            {store.description}
                        </p>
                    </motion.div>
                </div>

                {/* Filters */}
                <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                    <div className="relative max-w-sm flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input
                            data-testid="store-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={`Buscar en ${store.name}…`}
                            className="w-full h-10 pl-10 pr-4 rounded-lg bg-white border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                        />
                    </div>
                    <Select value={sort} onValueChange={setSort}>
                        <SelectTrigger
                            data-testid="store-sort-select"
                            className="w-full sm:w-[200px] h-10 border-zinc-200"
                        >
                            <SelectValue placeholder="Ordenar" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="recent">Más recientes</SelectItem>
                            <SelectItem value="popular">Más vendidos</SelectItem>
                            <SelectItem value="price_asc">
                                Precio: menor a mayor
                            </SelectItem>
                            <SelectItem value="price_desc">
                                Precio: mayor a menor
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Products */}
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6">
                    {filtered.map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                </div>

                {filtered.length === 0 && (
                    <div className="py-20 text-center text-sm text-zinc-500">
                        Sin productos para esa búsqueda.
                    </div>
                )}

                {/* Store info */}
                <div className="mt-20 mb-20 grid sm:grid-cols-3 gap-4">
                    <InfoCard
                        icon={Truck}
                        title="Métodos de envío"
                        body="Estafeta, FedEx, DHL y J&T. Cotización al checkout."
                    />
                    <InfoCard
                        icon={CreditCard}
                        title="Métodos de pago"
                        body="Tarjeta de crédito o débito y transferencia bancaria."
                    />
                    <InfoCard
                        icon={Clock}
                        title="Tiempos de procesamiento"
                        body="De 1 a 3 días hábiles antes del envío."
                    />
                </div>
            </div>
        </div>
    );
}

function InfoCard({ icon: Icon, title, body }) {
    return (
        <div className="rounded-xl border border-zinc-200 p-5 bg-white">
            <div className="w-9 h-9 rounded-lg bg-zinc-50 grid place-items-center">
                <Icon className="w-4 h-4 text-indigo-600" />
            </div>
            <h4 className="mt-3 text-sm font-semibold text-zinc-900">
                {title}
            </h4>
            <p className="mt-1 text-xs text-zinc-500 leading-relaxed">{body}</p>
        </div>
    );
}
